
import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, CheckCircle, Star, Award, Target } from 'lucide-react';
import { fadeIn, staggerContainer, getReducedMotionVariants } from '../utils/motion';
import { Card, CardContent } from '@/components/ui/card';

interface SuccessStoriesPageProps {
  reducedMotion: boolean;
}

const SuccessStoriesPage: React.FC<SuccessStoriesPageProps> = ({ reducedMotion }) => {
  const successMetrics = [
    {
      icon: Users,
      value: "10,000+",
      label: "Analyses Completed",
      description: "Homebuyers empowered with data-driven decisions"
    },
    {
      icon: CheckCircle,
      value: "99.9%",
      label: "Accuracy Rate",
      description: "Precise calculations you can trust"
    },
    {
      icon: TrendingUp,
      value: "$2.5M+",
      label: "Savings Identified",
      description: "Hidden costs and better terms discovered"
    },
    {
      icon: Award,
      value: "4.9/5",
      label: "User Satisfaction",
      description: "Rated by thousands of homebuyers"
    }
  ];

  const impactStories = [
    {
      icon: Target,
      title: "First-Time Buyer Success",
      stat: "85%",
      description: "Of users found properties within their budget they didn't think were possible",
      highlight: "SmartBuy Analyzer helped identify affordable options"
    },
    {
      icon: TrendingUp,
      title: "Investment Confidence",
      stat: "3x",
      description: "Investors using our analytics tools report 3x more confidence in their purchase decisions",
      highlight: "Investor Analytics + Community Intelligence combination"
    },
    {
      icon: CheckCircle,
      title: "Time Savings",
      stat: "60%",
      description: "Average time saved in the homebuying research and analysis phase",
      highlight: "Integrated tools eliminate redundant work"
    }
  ];

  const testimonials = [
    {
      quote: "EquiForge showed me I could afford $50k more than I thought. The SmartBuy Analyzer compared my scenarios and made the decision crystal clear.",
      author: "Sarah M.",
      role: "First-Time Homebuyer",
      rating: 5
    },
    {
      quote: "As an investor, the analytics suite is invaluable. ROI projections, community data, and tax records all in one place saved me weeks of research.",
      author: "Michael T.",
      role: "Real Estate Investor",
      rating: 5
    },
    {
      quote: "C.L.A.R.A. answered my questions at midnight when I was reviewing offers. Having 24/7 AI support made me feel confident in my decisions.",
      author: "Jennifer L.",
      role: "Remote Homebuyer",
      rating: 5
    }
  ];

  return (
    <div className="space-y-16">
      {/* Header */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-4"
      >
        <h2 className="text-4xl font-bold bg-gradient-to-r from-yellow-400 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
          Proven Results
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Real data, real impact, real success stories from thousands of homebuyers
        </p>
      </motion.div>

      {/* Success Metrics Grid */}
      <motion.div
        variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {successMetrics.map((metric, index) => (
          <motion.div
            key={metric.label}
            variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.5), reducedMotion)}
            whileHover={!reducedMotion ? { y: -5, transition: { type: "spring", stiffness: 300 } } : {}}
          >
            <Card className="h-full">
              <CardContent className="p-6 space-y-4 text-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto">
                  <metric.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="text-3xl font-bold text-primary">{metric.value}</div>
                <div className="font-semibold">{metric.label}</div>
                <p className="text-sm text-muted-foreground">{metric.description}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Impact Stories */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0.2, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="space-y-6"
      >
        <h3 className="text-2xl font-bold text-center">Impact Stories</h3>
        <motion.div
          variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {impactStories.map((story, index) => (
            <motion.div
              key={story.title}
              variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.4), reducedMotion)}
            >
              <Card className="h-full">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10">
                      <story.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h4 className="font-semibold text-lg">{story.title}</h4>
                  </div>
                  <div className="text-4xl font-bold text-primary">{story.stat}</div>
                  <p className="text-muted-foreground">{story.description}</p>
                  <div className="pt-2 border-t border-border">
                    <p className="text-sm italic text-primary/80">{story.highlight}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Testimonials */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0.3, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="space-y-6"
      >
        <h3 className="text-2xl font-bold text-center">What Users Say</h3>
        <motion.div
          variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.4), reducedMotion)}
            >
              <Card className="h-full">
                <CardContent className="p-6 space-y-4">
                  <div className="flex gap-1">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-muted-foreground italic">"{testimonial.quote}"</p>
                  <div className="pt-4 border-t border-border">
                    <div className="font-semibold">{testimonial.author}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Bottom CTA */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0.4, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-4 pt-8 border-t border-border"
      >
        <h3 className="text-2xl font-bold">Join Thousands of Successful Homebuyers</h3>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Start your homebuying journey with confidence. Access all tools and features completely free.
        </p>
      </motion.div>
    </div>
  );
};

export default SuccessStoriesPage;
